import { useContext, useEffect, useState } from 'react'

import Container from '../basic-components/Container'
import Content from '../basic-components/Content'
import { Footer, Header } from '../basic-components/HeaderFooter'
import HeaderTitle from '../basic-components/HeaderTitle'
import Modal from '../basic-components/Modal'

import BackIcon from '../icons/BackIcon'
import CrosshairsIcon from '../icons/CrosshairsIcon'
import EditIcon from '../icons/EditIcon'
import EnvelopeIcon from '../icons/EnvelopeIcon'
import SaveIcon from '../icons/SaveIcon'
import TrashIcon from '../icons/TrashIcon'
import IconContainer from '../icons/IconContainer'

import MapContext from '../context/MapContext'
import NotesContext from '../context/NotesContext'
import QueryContext from '../context/QueryContext';

import ConfirmDialog from './ConfirmDialog'
import NoteText from './NoteText'
import NoteTitle from './NoteTitle'


export default function NotePage({ currentNoteId, gotoMap, onBackClick }) {
  const [ editing, setEditing ] = useState(false);
  const [ modalStatus, setModalStatus ] = useState('');
  const [ noteTitle, setNoteTitle ] = useState('');
  const [ noteText, setNoteText ] = useState('');
  const [ noteEdited, setNoteEdited ] = useState(false);

  const { moveTo, zoomCloser, noteMarkers } = useContext(MapContext);
  const { notes, setNotes, noteStore } = useContext(NotesContext);
  const { setQueryData, setQueryLocation } = useContext(QueryContext);

  //console.warn('*** rendering NotePage *****************');
  //console.log(currentNoteId);

  // note page icon click handlers
  const backClickHandler = () => {
    if (noteEdited) {
      setModalStatus('exit-dialog');
    } else {
      setEditing(false);
    }
  }

  const crosshairsClickHandler = () => {
    //console.log(note);

    setQueryData(null);
    setQueryLocation(null);

    // check for location
    if (note.x && note.y) {
      const coordinates = [note.x, note.y];
      // TODO: do we clear them or leave them all on the map?
      noteMarkers.clear();
      noteMarkers.add(currentNoteId, coordinates, note.title);

      moveTo(coordinates);
      zoomCloser();
    }
    // go to map
    gotoMap(true);
    // show pin with title as label
  }

  const editClickHandler = () => { setEditing(true); }

  const emailClickHandler = () => {
    // create email subject and body
    const subject = "Note: #" + currentNoteId + " - " + note.title;
    const bodyText = "\r\nLatitude: " + note.latitude.toFixed(5) +
        "\r\nLongitude: " + note.longitude.toFixed(5) +
        "\r\n \r\nNC State Plane X: " + note.x.toFixed(0) +
        "\r\nNC State Plane Y: " + note.y.toFixed(0) +
        "\r\n" + note.text;

    //console.log(subject);
    //console.log(bodyText);

    // open email program
    window.location.href = 'mailto:?subject=' + encodeURIComponent(subject) +
        '&body=' + encodeURIComponent(bodyText);
  }

  const trashClickHandler = () => { setModalStatus('trash-dialog'); }

  // edit handlers
  const noteTitleChangeHandler = (value) => {
    setNoteTitle(value);
    setNoteEdited(true);
  }

  const noteTextChangeHandler = (value) => {
    setNoteText(value);
    setNoteEdited(true);
  }

  const saveChanges = () => {
    const updatedNote = {...note, title: noteTitle, text: noteText}
    const newNotes = noteStore.updateNote(notes, currentNoteId, updatedNote);
    setNotes(newNotes);
  }

  const saveClickHandler = () => {
    saveChanges();
    setNoteEdited(false);
    // TODO: do we really want to go back to non-editing
    setEditing(false);
  }

  // modal exit handlers
  const noClickHandler = () => {
    switch (modalStatus) {
      case 'exit-dialog':
        // reset changes to previous values
        setNoteTitle(note.title);
        setNoteText(note.text);
        setEditing(false);
        break;
      case 'trash-dialog':
      default:
      // do nothing
    }
    setModalStatus('');
  }

  const yesClickHandler = () => {
    switch (modalStatus) {
      case 'exit-dialog':
        // save note changes
        saveChanges();
        setNoteEdited(false);
        setEditing(false);
        break;
      case 'trash-dialog':
        // delete note
        const newNotes = noteStore.deleteNote(notes, currentNoteId);
        setNotes(newNotes);
        // go back to notes page
        onBackClick();
        break;
      default:
        //console.error('in NotePage.js - modalStatus = ' + modalStatus);
    }
    setModalStatus('');
  }


  const note = notes[currentNoteId];

  //console.error('before useEffect');
  //console.log(note);

  useEffect(() => {
    //console.error('useEffect in NotePage *************************');
    setNoteTitle(note.title);
    setNoteText(note.text)
  }, [currentNoteId, note])

  const questions = {
    "exit-dialog": "Do you want to save the changes?",
    "trash-dialog": "Do you really want to delete this note?",
  }


  return (
    <>
    <Container>
      <Header>
        <IconContainer>
          <BackIcon onClick={editing ? backClickHandler : onBackClick}/>
        </IconContainer>
        <HeaderTitle>
          <strong>{editing ? 'Edit Note' : note.title}</strong>
        </HeaderTitle>
        <IconContainer justify="right">

          {editing ? (
              <SaveIcon
                isDisabled={!noteEdited}
                onClick={saveClickHandler}
              />
          ) : (
            <>
              <EditIcon onClick={editClickHandler} />
              <EnvelopeIcon onClick={emailClickHandler} />
              <TrashIcon onClick={trashClickHandler} />
            </>
          )}

        </IconContainer>
      </Header>

      <Content style={{display: 'flex', flexDirection: 'column', textAlign: 'center'}}>
        <NoteTitle
          editing={editing}
          value={noteTitle}
          onChange={noteTitleChangeHandler}
        />
        <NoteText
          editing={editing}
          value={noteText}
          onChange={noteTextChangeHandler}
          autoFocus={editing ? true : null}
        />
      </Content>

      {editing ? null : (
        <Footer>
          <HeaderTitle>
            Lat: {note.latitude.toFixed(5)} &nbsp; Long: {note.longitude.toFixed(5)}
          </HeaderTitle>
          <IconContainer justify="right">
            <CrosshairsIcon onClick={crosshairsClickHandler}/>
          </IconContainer>
        </Footer>
      )}

    </Container>

    <Modal
      open={modalStatus !== ''}
      showCloseIcon={false}
      closeOnOverlayClick={false}
      onClose={() => setModalStatus('')}
    >
      <ConfirmDialog
        question={questions[modalStatus]}
        onNoClick={noClickHandler}
        onYesClick={yesClickHandler}
      />
    </Modal>
    </>
  );
}
